import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Spinner, Alert, Button, Badge, Breadcrumb, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import InterestModal from "../../component/InterestModal";

const CourseDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { publicAuth } = useApi();

  const [course, setCourse] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Value-added services state
  const [selectedServices, setSelectedServices] = useState({
    licenseAssistance: false,
    ownCarAssistance: false,
    upgradedCarTraining: false
  });

  const publicAuthRef = useRef(publicAuth);
  useEffect(() => { publicAuthRef.current = publicAuth; }, [publicAuth]);

  const lastFetchedSlugRef = useRef(null);

  const formatINR = (value) => {
    try {
      const num = Number(value);
      if (Number.isNaN(num)) return "₹--";
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(num);
    } catch {
      return "₹--";
    }
  };

  

  const imgSrc = useMemo(() => {
    const apiBase = process.env.REACT_APP_IMAGE_URL?.replace("/API/index.php", "/API") || "";
    return course?.image ? `${apiBase}/${course.image}` : `${process.env.PUBLIC_URL}/assets/images/course1.png`;
  }, [course]);

  // Get course price for selected city
  const getCoursePrice = () => {
    if (!course?.prices || !selectedCity) return null;
    return course.prices.find(price => price.city_id === parseInt(selectedCity));
  };

  const selectedPrice = getCoursePrice();

  // Get course price for a specific city
  const getCityPrice = (cityId) => {
    if (!course?.prices) return null;
    return course.prices.find(price => price.city_id === parseInt(cityId));
  };

  // Calculate total price including selected services
  const calculateTotalPrice = () => {
    if (!selectedPrice) return 0;
    
    let total = selectedPrice.total_price || selectedPrice.base_price;
    
    if (selectedServices.licenseAssistance) total += 1000;
    if (selectedServices.ownCarAssistance) total += 1000;
    if (selectedServices.upgradedCarTraining) total += 500;
    
    return total;
  };

  const totalPrice = calculateTotalPrice();

  // Calculate base course price (without services)
  const getBaseCoursePrice = () => {
    if (!selectedPrice) return 0;
    return selectedPrice.total_price || selectedPrice.base_price;
  };

  // Handle service checkbox change
  const handleServiceChange = (service) => {
    setSelectedServices(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
  };

  // Fetch cities
  const fetchCities = async () => {
    try {
      setCitiesLoading(true);
      const response = await publicAuthRef.current("cities", "GET");
      if (response?.data?.status && response?.data?.data) {
        setCities(response.data.data);
        
        // Check if city parameter exists in URL
        const urlParams = new URLSearchParams(window.location.search);
        const cityParam = urlParams.get('city');
        
        if (cityParam) {
          // Find city by name (case insensitive)
          const foundCity = response.data.data.find(city => 
            city.name.toLowerCase() === cityParam.toLowerCase()
          );
          if (foundCity) {
            setSelectedCity(foundCity.id.toString());
            console.log("Auto-selected city:", foundCity.name, "ID:", foundCity.id);
          } else if (response.data.data.length > 0) {
            // Fallback to first city if specified city not found
            setSelectedCity(response.data.data[0].id.toString());
            console.log("City not found, fallback to:", response.data.data[0].name);
          }
        } else if (response.data.data.length > 0) {
          // Auto-select first city if no city parameter
          setSelectedCity(response.data.data[0].id.toString());
          console.log("No city param, auto-selected:", response.data.data[0].name);
        }
      }
    } catch (err) {
      console.error("Failed to fetch cities:", err);
    } finally {
      setCitiesLoading(false);
    }
  };

  // Fetch course with city parameter
  const fetchCourseWithCity = async (cityName = null) => {
    const controller = new AbortController();
    
    try {
      setLoading(true);
      setError(null);

      // Get city parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const cityParam = cityName || urlParams.get('city');
      
      console.log("Fetching course with params - Slug:", slug, "City:", cityParam);

      // Build API URL with city parameter
      let apiUrl = `course/${encodeURIComponent(slug)}`;
      
      // Add city parameter to API call if available
      if (cityParam) {
        apiUrl += `?city=${encodeURIComponent(cityParam)}`;
        console.log("API URL with city:", apiUrl);
      } else {
        console.log("API URL without city:", apiUrl);
      }

      let response = await publicAuthRef.current(
        apiUrl,
        "GET",
        undefined,
        { signal: controller.signal }
      );

      // Fallback to courses endpoint if needed
      if (!response?.data?.status && !controller.signal.aborted) {
        console.log("Trying fallback API endpoint");
        let fallbackUrl = `courses?slug=${encodeURIComponent(slug)}`;
        if (cityParam) {
          fallbackUrl += `&city=${encodeURIComponent(cityParam)}`;
        }
        response = await publicAuthRef.current(
          fallbackUrl,
          "GET",
          undefined,
          { signal: controller.signal }
        );
      }

      if (controller.signal.aborted) return;

      const payload = response?.data;
      if (payload?.status && payload?.data) {
        const item = Array.isArray(payload.data) ? payload.data[0] : payload.data;
        if (!item) throw new Error("Course not found");
        
        console.log("Course data received:", item);
        
        // Transform the API response to match expected format
        const transformedCourse = {
          ...item,
          // Ensure prices array exists for compatibility
          prices: item.price ? [item.price] : (item.prices || [])
        };
        
        setCourse(transformedCourse);
        
        // Fetch cities after course is loaded
        await fetchCities();
      } else {
        const msg = payload?.message || "Failed to load course";
        throw new Error(msg);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error("Error fetching course:", err);
        setError(err?.message || "Something went wrong");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }

    return () => controller.abort();
  };

  useEffect(() => {
    if (lastFetchedSlugRef.current === slug) return;
    lastFetchedSlugRef.current = slug;

    fetchCourseWithCity();
  }, [slug]);

  // Refetch course when city changes
  useEffect(() => {
    if (selectedCity && cities.length > 0) {
      const selectedCityObj = cities.find(city => city.id === parseInt(selectedCity));
      if (selectedCityObj) {
        console.log("City changed, refetching course for:", selectedCityObj.name);
        // Update URL without page reload
        const newUrl = `${window.location.pathname}?city=${encodeURIComponent(selectedCityObj.name)}`;
        window.history.replaceState(null, '', newUrl);
        
        // Refetch course with new city
        fetchCourseWithCity(selectedCityObj.name);
      }
    }
  }, [selectedCity]);

  if (loading) {
    return (
      <section className="py-5 my-5">
        <Container className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading course…</p>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 my-5">
        <Container className="py-5">
          <Alert variant="danger" className="rounded-4 border-0 shadow-sm">
            <h5 className="mb-1">We couldn't load this course</h5>
            <p className="mb-0 text-muted">{error}</p>
            <Button variant="outline-secondary" className="mt-3" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </Alert>
        </Container>
      </section>
    );
  }

  if (!course) return null;

  return (
    <section className="py-5 my-5 position-relative">
      <Container>
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item onClick={() => navigate("/")} linkAs="button">Home</Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate("/courses")} linkAs="button">Courses</Breadcrumb.Item>
          <Breadcrumb.Item active>{course.title}</Breadcrumb.Item>
        </Breadcrumb>

        <Row className="g-5 align-items-start">
          <Col lg={6}>
            <div className="position-relative rounded-4 overflow-hidden shadow-lg">
              <img
                src={imgSrc}
                alt={course.title}
                className="img-fluid w-100"
                style={{ height: 440, objectFit: "cover" }}
                onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/images/course2.png`; }}
              />
              <div className="price-chip shadow-sm">
                <span className="d-block small text-muted">Price</span>
                <span className="fw-bold">
                  {selectedPrice ? formatINR(totalPrice) : "₹--"}
                </span>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge bg="primary" className="rounded-pill">Learner Course</Badge>
              {course.status === "published" ? (
                <Badge bg="success" className="rounded-pill">Active</Badge>
              ) : (
                <Badge bg="secondary" className="rounded-pill">Inactive</Badge>
              )}
            </div>

            <h1 className="fw-bold mb-3">{course.title}</h1>
            <p className="lead text-muted">{course.description}</p>

            <Row className="g-3 my-4">
              <Col sm={6}>
                <div className="p-3 bg-light rounded-4 d-flex align-items-center gap-3 shadow-sm">
                  <i className="bi bi-journal-text text-primary fs-4"></i>
                  <div>
                    <div className="small text-muted">Theory Session</div>
                    <div className="fw-bold">{course.duration} Hours</div>
                  </div>
                </div>
              </Col>
              <Col sm={6}>
                <div className="p-3 bg-light rounded-4 d-flex align-items-center gap-3 shadow-sm">
                  <i className="bi bi-steering-wheel text-success fs-4"></i>
                  <div>
                    <div className="small text-muted">Practical Session</div>
                    <div className="fw-bold">{course.practical} Hours</div>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="alert alert-info border-0 rounded-4 mb-4">
              <i className="bi bi-info-circle-fill me-2" aria-hidden /> Driving route will be charged separately.
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => navigate(-1)}>Back</Button>
              <Button 
                  variant="primary" 
                  className="rounded-pill px-4" 
                  onClick={() => setShowInterestModal(true)}
                  disabled={!selectedCity}
                >
                  Register Now
              </Button>
            </div>
          </Col>
        </Row>

        {/* Program Price Section */}
        <Row className="mt-5 pt-5">
          <Col lg={10} className="mx-auto">
            <div className="bg-light rounded-4 p-4 mb-5 shadow-sm">
              <Row className="align-items-center">
                <Col md={4}>
                  <h5 className="mb-0 fw-bold">Program Price</h5>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-medium">Select City</Form.Label>
                    <Form.Select 
                      value={selectedCity} 
                      onChange={(e) => setSelectedCity(e.target.value)}
                      disabled={citiesLoading}
                    >
                      <option value="">Choose a city</option>
                      {cities.map(city => {
                        const cityPrice = getCityPrice(city.id);
                        return (
                          <option key={city.id} value={city.id}>
                            {city.name} {cityPrice ? `- ${formatINR(cityPrice.total_price || cityPrice.base_price)}` : ''}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <div className="text-end">
                    {selectedPrice ? (
                      <>
                        <div className="fw-bold fs-3 text-primary">
                          {formatINR(totalPrice)}
                        </div>
                        <div className="small text-muted">
                          (Prices are exclusive of 18% GST)
                        </div>
                        {totalPrice !== getBaseCoursePrice() && (
                          <div className="small text-success">
                            Includes selected services
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted">Select a city to see price</div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Value Added Services Section */}
        <Row className="mt-4">
          <Col lg={10} className="mx-auto">
            <div className="text-center mb-5">
              <h2 className="fw-bold mb-3">Make the Most of Your Driving Course</h2>
              <p className="text-muted lead">Value-Added services customized for you.</p>
            </div>

            <div className="bg-white rounded-4 shadow-sm overflow-hidden">
              <table className="table table-borderless mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 py-3 ps-4">Service</th>
                    <th className="border-0 py-3">Description</th>
                    <th className="border-0 py-3 text-center">Price</th>
                    <th className="border-0 py-3 pe-4 text-center" style={{ width: '100px' }}>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {/* License Assistance */}
                  <tr className="border-bottom">
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="service-icon">
                          <i className="bi bi-file-earmark-check-fill text-primary fs-4"></i>
                        </div>
                        <div className="fw-semibold">License Assistance*</div>
                      </div>
                    </td>
                    <td className="py-3 text-muted">
                      Get pick & drop facility and vehicle assistance for license test at RTO
                    </td>
                    <td className="py-3 text-center fw-bold">₹ 1,000*</td>
                    <td className="pe-4 py-3 text-center">
                      <Form.Check
                        type="checkbox"
                        checked={selectedServices.licenseAssistance}
                        onChange={() => handleServiceChange('licenseAssistance')}
                        disabled={!selectedCity}
                        className="d-inline-flex"
                      />
                    </td>
                  </tr>
                  
                  {/* Assistance on own car */}
                  <tr className="border-bottom">
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="service-icon">
                          <i className="bi bi-car-front-fill text-success fs-4"></i>
                        </div>
                        <div className="fw-semibold">Assistance on own car*</div>
                      </div>
                    </td>
                    <td className="py-3 text-muted">
                      Get practical sessions (3 hours) on your own car, which further enhances the learning
                    </td>
                    <td className="py-3 text-center fw-bold">₹ 1,000*</td>
                    <td className="pe-4 py-3 text-center">
                      <Form.Check
                        type="checkbox"
                        checked={selectedServices.ownCarAssistance}
                        onChange={() => handleServiceChange('ownCarAssistance')}
                        disabled={!selectedCity}
                        className="d-inline-flex"
                      />
                    </td>
                  </tr>
                  
                  {/* Training on Upgraded Car Models */}
                  <tr>
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="service-icon">
                          <i className="bi bi-arrow-up-circle-fill text-warning fs-4"></i>
                        </div>
                        <div className="fw-semibold">Training on Upgraded Car Models*</div>
                      </div>
                    </td>
                    <td className="py-3 text-muted">
                      Learn to drive on upgraded models e.g. Swift, Dzire, Vitara Brezza and Ertiga
                    </td>
                    <td className="py-3 text-center fw-bold">₹ 500*</td>
                    <td className="pe-4 py-3 text-center">
                      <Form.Check
                        type="checkbox"
                        checked={selectedServices.upgradedCarTraining}
                        onChange={() => handleServiceChange('upgradedCarTraining')}
                        disabled={!selectedCity}
                        className="d-inline-flex"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Price Breakdown Section */}
            {selectedCity && (
              <div className="mt-4 p-4 bg-light rounded-4">
                <h6 className="fw-bold mb-3">Price Breakdown</h6>
                <Row className="g-3">
                  <Col xs={12}>
                    <div className="d-flex justify-content-between border-bottom pb-2">
                      <span>Course Fee ({cities.find(c => c.id === parseInt(selectedCity))?.name})</span>
                      <span className="fw-semibold">{formatINR(getBaseCoursePrice())}</span>
                    </div>
                  </Col>
                  
                  {selectedServices.licenseAssistance && (
                    <Col xs={12}>
                      <div className="d-flex justify-content-between border-bottom pb-2">
                        <span className="text-success">+ License Assistance</span>
                        <span className="fw-semibold text-success">+ ₹1,000</span>
                      </div>
                    </Col>
                  )}
                  {selectedServices.ownCarAssistance && (
                    <Col xs={12}>
                      <div className="d-flex justify-content-between border-bottom pb-2">
                        <span className="text-success">+ Own Car Assistance</span>
                        <span className="fw-semibold text-success">+ ₹1,000</span>
                      </div>
                    </Col>
                  )}
                  {selectedServices.upgradedCarTraining && (
                    <Col xs={12}>
                      <div className="d-flex justify-content-between border-bottom pb-2">
                        <span className="text-success">+ Upgraded Car Training</span>
                        <span className="fw-semibold text-success">+ ₹500</span>
                      </div>
                    </Col>
                  )}
                  
                  <Col xs={12}>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between fw-bold fs-5">
                      <span>Total Amount:</span>
                      <span className="text-primary">{formatINR(totalPrice)}</span>
                    </div>
                    <div className="small text-muted mt-1">
                      *Exclusive of 18% GST
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Disclaimer Section */}
            <div className="mt-4 p-4 bg-light rounded-4">
              <h6 className="fw-bold mb-3">Disclaimer</h6>
              <ul className="list-unstyled small text-muted mb-0">
                <li className="mb-2">• License assistance is subject to RTO rooms.</li>
                <li className="mb-2">• Training on upgraded car models is based on availability as per driving schools.</li>
                <li className="mb-2">• Value-added services are only applicable to students who have enrolled at Maruti Suzuki Driving School.</li>
                <li className="mb-2">• The regulations of State Government/ local laws/ Transport Department circulars etc. in relation to MSDS operations shall prevail over the terms and conditions of MSDS in so far there is a discrepancy.</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>
      <style>{`
        .price-chip {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: .5rem .75rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        
        .table td, .table th {
          vertical-align: middle;
        }
        
        .bg-light {
          background-color: #f8f9fa !important;
        }
        
        .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        
        .form-check-input:disabled {
          background-color: #e9ecef;
          border-color: #dee2e6;
        }
        
        .service-icon {
          flex-shrink: 0;
          width: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .form-check {
          margin-bottom: 0;
        }
      `}</style>
      <InterestModal
        show={showInterestModal}
        onClose={() => setShowInterestModal(false)}
        courseId={course?.id}
      />
    </section>
  );
};

export default CourseDetails;
